import { classifyUrl, extractLinks, extractMedia } from "@/utils/content"
import { tagValues, type NostrEvent } from "@/nostr/types"

/**
 * Media axis of the unified filter architecture.
 *
 * Classification is derived from the event itself (no extra requests):
 *
 * - URLs in the content, classified with the same rules `NoteContent` renders
 *   (`.png` / `.mp4` / `.mp3` …), including NIP-92 `imeta` alt text;
 * - NIP-92 `imeta` tags that declare a `url` and a MIME `m` field;
 * - NIP-23 article media tags (`image`, `thumb`);
 * - every other URL in the content counts as a plain link, never as media.
 *
 * A post can carry several media types; `MEDIA_FILTERS` matches on the union.
 */
export const MEDIA_FILTERS = ["text", "image", "video", "audio", "link", "media"] as const

export type MediaFilter = (typeof MEDIA_FILTERS)[number]

export type MediaKind = "image" | "video" | "audio"

export interface MediaClassification {
  /** Media kinds attached to the post (de-duplicated). */
  kinds: MediaKind[]
  /** Plain links (media URLs are excluded, so a link is a link). */
  links: string[]
  hasMedia: boolean
  /** True when the post carries nothing but text. */
  isTextOnly: boolean
}

const MIME_PATTERNS: Array<[RegExp, MediaKind]> = [
  [/^image\//i, "image"],
  [/^video\//i, "video"],
  [/^audio\//i, "audio"],
]

/** Maps a MIME type (`image/png`) to a media kind. */
export function mediaKindFromMime(mime: string | undefined): MediaKind | undefined {
  if (!mime) return undefined
  for (const [pattern, kind] of MIME_PATTERNS) {
    if (pattern.test(mime.trim())) return kind
  }
  return undefined
}

/** Maps a URL to a media kind using the shared content classifier. */
function mediaKindOfUrl(url: string): MediaKind | undefined {
  const segment = classifyUrl(url)
  if (segment.type === "image" || segment.type === "video" || segment.type === "audio") return segment.type
  return undefined
}

function imetaValue(tag: string[], name: string): string | undefined {
  const entry = tag.find((item) => item.startsWith(`${name} `))
  return entry ? entry.slice(name.length + 1).trim() : undefined
}

/** Classifies the media a post carries. Pure and reusable. */
export function classifyMedia(event: NostrEvent): MediaClassification {
  const kinds = new Set<MediaKind>()

  // Content URLs (plus NIP-92 alt text for the ones that also appear inline).
  for (const item of extractMedia(event.content, event.tags)) kinds.add(item.type)

  // NIP-92 attachments that are only declared in the tag.
  for (const tag of event.tags) {
    if (tag[0] !== "imeta") continue
    const url = imetaValue(tag, "url")
    if (!url) continue
    const kind = mediaKindFromMime(imetaValue(tag, "m")) ?? mediaKindOfUrl(url)
    if (kind) kinds.add(kind)
  }

  // NIP-23 long form articles reference their assets with dedicated tags.
  for (const name of ["image", "thumb"]) {
    for (const url of tagValues(event, name)) {
      kinds.add(mediaKindOfUrl(url) ?? "image")
    }
  }

  const links = extractLinks(event.content)
  const mediaKinds = [...kinds]
  return {
    kinds: mediaKinds,
    links,
    hasMedia: mediaKinds.length > 0,
    isTextOnly: mediaKinds.length === 0 && links.length === 0,
  }
}

/** The media filters a post satisfies (used for chips and for the "N kinds" hint). */
export function mediaTypesOf(event: NostrEvent): MediaFilter[] {
  const { kinds, links, isTextOnly } = classifyMedia(event)
  const types: MediaFilter[] = [...kinds]
  if (links.length > 0) types.push("link")
  if (isTextOnly) types.push("text")
  return types
}

/**
 * True when the post matches the selection. An empty selection matches
 * everything; several selections match when any of them matches, so a post with
 * a picture *and* a link satisfies both "Images" and "Links".
 */
export function matchesMediaFilters(
  event: NostrEvent,
  filters?: readonly MediaFilter[],
): boolean {
  if (!filters || filters.length === 0) return true
  const { kinds, links, isTextOnly, hasMedia } = classifyMedia(event)
  return filters.some((filter) => {
    if (filter === "text") return isTextOnly
    if (filter === "media") return hasMedia
    if (filter === "link") return links.length > 0
    return kinds.includes(filter)
  })
}
